import { useState } from "react";
import ReactGA from "react-ga4";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useCurrentUser, useLogout } from "utils/hooks";
import { formatInput } from "utils/helpers";
import useFetchOrganizations from "./useFetchOrganizations";
import { addOrganizationAction } from "store/actions/organizations";
import { useNavigate } from "react-router-dom";

const useCreateOrganization = () => {
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { fetchOrganizations } = useFetchOrganizations();
  const { user } = useCurrentUser();
  const history = useNavigate();

  const submitCreateOrganization = (args: any) => {
    if (!args) {
      return null;
    }

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                createOrganization(organization: {
                  name: "${formatInput(args.name)}",
                  description: "${formatInput(args.description)}",
                  location: "${formatInput(args.location)}",
                  address: {
                    address1: "${formatInput(args.address1)}",
                    address2: "${formatInput(args.address2)}",
                    city: "${formatInput(args.city)}",
                    state: "${formatInput(args.state?.value)}",
                    country: "${formatInput(args.country?.value)}",
                  }
                }) {
                  uuid
                  name
                  description
                  slug
                  location
                  address {
                    address1
                    address2
                    city
                    state
                    country
                  }
                  status
                  createdAt
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "create_organizations",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                setIsSubmitSuccessful(true);
                toast.warn(`For security purposes please log in again.`);
                logOut();
              }
              if (
                error.errors[0].message.includes("ORGANIZATION_LIMIT_REACHED")
              ) {
                toast.warn(`Organization limit reached, please upgrade`);
                setIsSubmitSuccessful(true);
                resolve();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              setIsSubmitSuccessful(true);
              resolve();
              return true;
            } else {
              fetchOrganizations();
              toast("Your organization has been created");
              setIsSubmitSuccessful(true);
              resolve();
              ReactGA.event("organization_created_success", {
                user_id: user?.id,
                organization_id: resData.data.createOrganization.uuid,
              });
              dispatch(addOrganizationAction(resData.data.createOrganization));
              fetchOrganizations();
              return history(
                `/orgs/active/${resData.data.createOrganization.slug}/workbook`
              );
            }
          })
          .catch((err) => {
            setIsSubmitSuccessful(true);
            console.log(err);
          });
      }, 100);
    });
  };

  return {
    submitCreateOrganization,
    isSubmitSuccessful,
  };
};

export default useCreateOrganization;
